'use client';

import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { usePathname, useRouter } from '@/src/i18n/navigation';
import type { PartialDeep } from 'type-fest';
import type { HpResolvedStateDto } from '@zg-rentals/ts-hotpads-api-client';
import { routing } from '@/src/i18n/routing';
import { useSrpPageViewCoordinator } from '@/src/analytics/SrpPageViewCoordinator';
import { useAllRentalsSlug } from '@/src/lib/seo/AllRentalsSlugProvider';
import { computeCanonicalSrpUrl } from './srpCanonicalUrl';

/**
 * Morphs the SRP URL to the BE-resolved slug as filters change (promote AND
 * demote), via a single `router.replace` that keeps the query (no param strip).
 * App Router keeps the current content during the RSC fetch → no blank; the
 * server stays promote-only so it never strip-redirects mid-nav. h1/meta come
 * from the new slug's server render. The load-time equivalent is the SRP route's
 * redirect, so bots/no-JS land on the canonical directly.
 */
export function useSrpSlugCanonicalization(
  resolved: PartialDeep<HpResolvedStateDto> | undefined,
  requestKey: string,
): void {
  const router = useRouter();
  const pathname = usePathname() ?? '/';
  const searchParams = useSearchParams();
  const { registerCanonicalRemount } = useSrpPageViewCoordinator();
  const allRentalsSlug = useAllRentalsSlug();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const canonical = computeCanonicalSrpUrl({
      pathname,
      search: window.location.search,
      locales: routing.locales,
      resolvedSlug: resolved?.resolvedSlug,
      allRentalsSlug,
    });
    if (canonical) {
      registerCanonicalRemount(requestKey, canonical);
      router.replace(canonical, { scroll: false });
    }
    // searchParams is in deps so the effect re-runs after a shallow filter (query) change.
  }, [registerCanonicalRemount, requestKey, resolved, pathname, searchParams, router, allRentalsSlug]);
}
